import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMsal } from "@azure/msal-react";

import {
  Box,
  MenuItem,
  Divider,
  Chip,
  Tooltip,
  Button,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  LinearProgress,
} from "@mui/material";

import { useNavigate } from "react-router-dom";

import { logout } from "../../actions/userActions";

import {
  requestCredits,
  resetCreditState,
  updateCredits,
} from "../../actions/creditActions";

import TermsDialog from "../../dialogs/TermsDialog";
import PrivacyPolicyDialog from "../../dialogs/PrivacyPolicyDialog";
import CreditsInfoDialog from "../../dialogs/CreditsInfoDialog";
import CongratulationDialog from "../../dialogs/CongratulationDialog";
import FeedbackDialog from "../../dialogs/FeedbackDialog";
import ProfileModal from "../../dialogs/ProfileDialog";

import {
  HelpCircle,
  Info,
  Bell,
  User,
  LogOut,
  FileText,
  Shield,
  CreditCard,
  CheckCircle,
  Zap,
  AlertTriangle,
} from "react-feather";

import { ToastContainer } from "react-toastify";

const Header = ({ handleSidebarToggle }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { instance, accounts } = useMsal();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [openTermsDialog, setOpenTermsDialog] = useState(false);

  const [openPrivacyPolicyDialog, setOpenPrivacyPolicyDialog] = useState(false);

  const [loading, setLoading] = useState(false);

  const [congratsModalOpen, setCongratsModalOpen] = useState(false);

  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);

  const [profileOpen, setProfileOpen] = useState(false);

  const [showNotifications, setShowNotifications] = useState(false);

  const [showUserMenu, setShowUserMenu] = useState(false);

  const [creditSnackbar, setCreditSnackbar] = useState({
    open: false,
    severity: "info",
    title: "",
    message: "",
  });

  const previousCreditsRef = useRef(null);

  const userLogin = useSelector((state) => state.userLogin);

  const { userInfo } = userLogin;

  const { credits, creditRequestCount, isCreditsInitialized } = useSelector(
    (state) => state.credit,
  );

  const maxRefillCount = 3;

  const rawExpiry = userInfo?.planExpiration;

  const expiryDateObj = rawExpiry ? new Date(rawExpiry) : null;

  const isValidExpiry = expiryDateObj && !isNaN(expiryDateObj.getTime());

  const isDateActive = isValidExpiry && expiryDateObj > new Date();

  const isExpired = isValidExpiry && expiryDateObj <= new Date();

  const creditLimit = Number(userInfo?.credit || 0);

  const availableCredits =
    credits !== undefined && credits !== null ? Number(credits) : 0;

  const hasCredits = availableCredits > 0;

  const isSubscribed = isDateActive;

  const isCreditsExhausted = isDateActive && availableCredits <= 0;

  const creditPercentage =
    creditLimit > 0
      ? Math.min(
          100,
          Math.max(0, Math.round((availableCredits / creditLimit) * 100)),
        )
      : 0;

  const creditsUsed =
    creditLimit > 0 ? Math.max(0, creditLimit - availableCredits) : 0;

  const isLowCredits =
    creditLimit > 0 &&
    availableCredits > 0 &&
    availableCredits / creditLimit <= 0.2;

  const isCriticalCredits =
    creditLimit > 0 &&
    availableCredits > 0 &&
    availableCredits / creditLimit <= 0.1;

  const getFirstName = (name) => {
    return name ? name.split(" ")[0] : "";
  };

  const findAccountByHomeAccountId = (homeAccountId) => {
    return accounts.find((account) => account.homeAccountId === homeAccountId);
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCongratsModalClose = () => {
    setCongratsModalOpen(false);
  };

  const handleProfileClick = () => {
    setProfileOpen(true);
    setShowUserMenu(false);
  };

  const handleRequestCredit = useCallback(async () => {
    if (creditRequestCount < maxRefillCount) {
      setLoading(true);

      try {
        const response = dispatch(requestCredits(userInfo._id));

        if (response?.success) {
          setCongratsModalOpen(true);

          setShowUserMenu(false);
          setShowNotifications(false);

          dispatch(
            updateCredits({
              credits: response.credits,
              expirationDate: response.expirationDate,
              creditRequestCount: creditRequestCount + 1,
            }),
          );
        }
      } catch (error) {
        console.error("Failed to request credits:", error);
      } finally {
        setLoading(false);
      }
    }
  }, [dispatch, userInfo?._id, creditRequestCount]);

  const signOutClickHandler = (instance, accountToSignOut) => {
    instance.logout({
      account: accountToSignOut,
      postLogoutRedirectUri: "/",
    });
  };

  const logOut = () => {
    const homeAccountId = accounts[0]?.homeAccountId;

    const accountToSignOut = findAccountByHomeAccountId(homeAccountId);

    if (accountToSignOut) {
      signOutClickHandler(instance, accountToSignOut);
    } else {
      console.error("Account not found for the given homeAccountId");
    }
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    setShowNotifications(false);

    dispatch(logout());
    dispatch(resetCreditState());

    logOut();
  };

  const handleTermsClick = () => {
    setOpenTermsDialog(true);
    setShowUserMenu(false);
  };

  const handlePrivacyPolicyClick = () => {
    setOpenPrivacyPolicyDialog(true);
    setShowUserMenu(false);
  };

  const getTooltipTitle = () => {
    if (isCreditsExhausted) {
      return "Your credits are exhausted. Get more credits to continue using credit-based features.";
    }

    if (isCriticalCredits) {
      return `Only ${availableCredits} credits remaining. Your credits are almost exhausted.`;
    }

    if (isLowCredits) {
      return `${availableCredits} credits remaining. Your credits are running low.`;
    }

    if (creditRequestCount >= maxRefillCount) {
      return "You've reached the maximum number of refill requests. Please contact support for assistance.";
    }

    return `${availableCredits} credits remaining.`;
  };

  const showCreditNotification = (severity, title, message) => {
    setCreditSnackbar({
      open: true,
      severity,
      title,
      message,
    });
  };

  const handleCreditSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setCreditSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };

  useEffect(() => {
    if (!isCreditsInitialized) {
      return;
    }

    const currentCredits = availableCredits;

    const previousCredits = previousCreditsRef.current;

    if (previousCredits === null) {
      previousCreditsRef.current = currentCredits;

      return;
    }

    if (currentCredits === previousCredits) {
      return;
    }

    if (currentCredits > previousCredits) {
      previousCreditsRef.current = currentCredits;

      return;
    }

    if (previousCredits > 0 && currentCredits <= 0 && isDateActive) {
      showCreditNotification(
        "error",
        "Credits Exhausted",
        "You have used all your available credits. Your subscription is still active, but credit-consuming features are unavailable.",
      );
    } else if (
      creditLimit > 0 &&
      previousCredits / creditLimit > 0.1 &&
      currentCredits / creditLimit <= 0.1 &&
      currentCredits > 0
    ) {
      showCreditNotification(
        "warning",
        "Credits Almost Exhausted",
        `You have only ${currentCredits} credits remaining.`,
      );
    } else if (
      creditLimit > 0 &&
      previousCredits / creditLimit > 0.2 &&
      currentCredits / creditLimit <= 0.2 &&
      currentCredits > 0
    ) {
      showCreditNotification(
        "warning",
        "Credits Running Low",
        `You have ${currentCredits} credits remaining.`,
      );
    }

    previousCreditsRef.current = currentCredits;
  }, [availableCredits, creditLimit, isCreditsInitialized, isDateActive]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (showUserMenu && !event.target.closest(".user-menu-container")) {
        setShowUserMenu(false);
      }

      if (
        showNotifications &&
        !event.target.closest(".notifications-container")
      ) {
        setShowNotifications(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu, showNotifications]);

  const hasCreditNotification =
    isCreditsExhausted || isCriticalCredits || isLowCredits;

  const hasAnyNotification = isExpired || hasCreditNotification;

  let notificationTitle = "Notifications";

  let notificationMessage = "No new notifications";

  if (isExpired) {
    notificationTitle = "Plan Expired";

    notificationMessage = `Your subscription expired on ${expiryDateObj?.toLocaleDateString()}. Click here to renew.`;
  } else if (isCreditsExhausted) {
    notificationTitle = "Credits Exhausted";

    notificationMessage =
      "You have 0 credits remaining. Your subscription is still active, but credit-consuming features are unavailable.";
  } else if (isCriticalCredits) {
    notificationTitle = "Credits Almost Exhausted";

    notificationMessage = `Only ${availableCredits} credits remain. Consider getting more credits before they run out.`;
  } else if (isLowCredits) {
    notificationTitle = "Credits Running Low";

    notificationMessage = `You have ${availableCredits} credits remaining. Your credits are running low.`;
  }

  return (
    <>
      <Box
        position='fixed'
        top={0}
        left={0}
        right={0}
        zIndex={50}
        sx={{
          backgroundColor: "#FFFFFF",
          borderBottom: "1px solid #E5E7EB",
          height: "64px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: "100%",
            width: "100%",
            pr: {
              xs: 2,
              sm: 3,
              lg: 4,
            },
            pl: "15px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              onClick={handleSidebarToggle}
              sx={{
                p: 1,
                borderRadius: 1,
                color: "#6B7280",

                "&:hover": {
                  color: "#374151",
                  backgroundColor: "#F3F4F6",
                },
              }}
            >
              <Box
                component='span'
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <line x1='3' y1='6' x2='21' y2='6' />
                  <line x1='3' y1='12' x2='21' y2='12' />
                  <line x1='3' y1='18' x2='21' y2='18' />
                </svg>
              </Box>
            </IconButton>

            <Box
              sx={{
                mx: 2,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
              }}
              onClick={() => navigate("/app/home")}
            >
              <img
                src='/images/legalSearchLogo2.png'
                alt='LawGPT Logo'
                style={{
                  height: "40px",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Tooltip title='Feedback & Complaint' placement='bottom' arrow>
              <IconButton
                onClick={() => setOpenFeedbackDialog(true)}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  color: "#6B7280",

                  "&:hover": {
                    color: "#374151",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <HelpCircle size={20} />
              </IconButton>
            </Tooltip>

            <Tooltip title={getTooltipTitle()} placement='bottom' arrow>
              <IconButton
                onClick={handleModalOpen}
                sx={{
                  p: 1,
                  borderRadius: 1,

                  color: isCreditsExhausted
                    ? "#DC2626"
                    : isCriticalCredits
                      ? "#EF4444"
                      : isLowCredits
                        ? "#D97706"
                        : "#6B7280",

                  "&:hover": {
                    color: isCreditsExhausted
                      ? "#B91C1C"
                      : isCriticalCredits
                        ? "#DC2626"
                        : isLowCredits
                          ? "#B45309"
                          : "#374151",

                    backgroundColor: isCreditsExhausted
                      ? "#FEF2F2"
                      : isCriticalCredits
                        ? "#FEF2F2"
                        : isLowCredits
                          ? "#FFFBEB"
                          : "#F3F4F6",
                  },
                }}
              >
                <Info size={20} />
              </IconButton>
            </Tooltip>

            <Box
              className='notifications-container'
              sx={{
                position: "relative",
              }}
            >
              <IconButton
                onClick={() => setShowNotifications(!showNotifications)}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  color: "#6B7280",

                  "&:hover": {
                    color: "#374151",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    display: "inline-flex",
                  }}
                >
                  <Bell size={20} />

                  {hasAnyNotification && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -5,
                        right: -6,
                        backgroundColor: "#FFFFFF",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: "1px",
                        boxShadow: "0 0 0 2px #FFFFFF",
                      }}
                    >
                      <AlertTriangle
                        size={12}
                        style={{
                          color:
                            isExpired || isCreditsExhausted
                              ? "#DC2626"
                              : "#D97706",

                          fill:
                            isExpired || isCreditsExhausted
                              ? "#DC2626"
                              : "#D97706",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </IconButton>

              {showNotifications && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    mt: 1,

                    width: {
                      xs: 290,
                      sm: 340,
                    },

                    borderRadius: 2,

                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",

                    backgroundColor: "#FFFFFF",

                    border: "1px solid #E5E7EB",

                    zIndex: 1000,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #F3F4F6",
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      Notifications
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      maxHeight: 300,
                      overflowY: "auto",
                    }}
                  >
                    {hasAnyNotification ? (
                      <Box
                        onClick={() => {
                          setShowNotifications(false);

                          navigate("/app/subscriptions");
                        }}
                        sx={{
                          px: 2.5,
                          py: 2,

                          backgroundColor:
                            isExpired || isCreditsExhausted
                              ? "#FEF2F2"
                              : "#FFFBEB",

                          borderBottom:
                            isExpired || isCreditsExhausted
                              ? "1px solid #FCA5A5"
                              : "1px solid #FCD34D",

                          cursor: "pointer",

                          display: "flex",
                          alignItems: "flex-start",

                          gap: 1.5,

                          "&:hover": {
                            backgroundColor:
                              isExpired || isCreditsExhausted
                                ? "#FEE2E2"
                                : "#FEF3C7",
                          },
                        }}
                      >
                        <AlertTriangle
                          size={18}
                          style={{
                            color:
                              isExpired || isCreditsExhausted
                                ? "#DC2626"
                                : "#D97706",

                            marginTop: 2,
                            flexShrink: 0,
                          }}
                        />

                        <Box>
                          <Typography
                            variant='body2'
                            sx={{
                              fontWeight: 600,

                              color:
                                isExpired || isCreditsExhausted
                                  ? "#991B1B"
                                  : "#92400E",
                            }}
                          >
                            {notificationTitle}
                          </Typography>

                          <Typography
                            variant='caption'
                            sx={{
                              color:
                                isExpired || isCreditsExhausted
                                  ? "#B91C1C"
                                  : "#B45309",

                              display: "block",

                              mt: 0.5,

                              lineHeight: 1.5,
                            }}
                          >
                            {notificationMessage}
                          </Typography>

                          <Typography
                            variant='caption'
                            sx={{
                              display: "block",

                              mt: 1,

                              fontWeight: 600,

                              color:
                                isExpired || isCreditsExhausted
                                  ? "#DC2626"
                                  : "#D97706",
                            }}
                          >
                            {isExpired
                              ? "Renew subscription →"
                              : "Get more credits →"}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          px: 3,
                          py: 2,
                          textAlign: "center",
                          color: "#6B7280",
                        }}
                      >
                        <Typography variant='body2'>
                          No new notifications
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            <Box
              className='user-menu-container'
              sx={{
                position: "relative",
              }}
            >
              <IconButton
                onClick={() => setShowUserMenu(!showUserMenu)}
                sx={{
                  p: 1,
                  borderRadius: 1,
                  color: "#6B7280",

                  "&:hover": {
                    color: "#374151",
                    backgroundColor: "#F3F4F6",
                  },
                }}
              >
                <User size={24} />
              </IconButton>

              {showUserMenu && (
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    mt: 1,

                    width: {
                      xs: 280,
                      sm: 310,
                    },

                    borderRadius: 2,
                    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
                    backgroundColor: "#FFFFFF",
                    border: "1px solid #E5E7EB",
                    zIndex: 1000,
                    py: 1,
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #F3F4F6",
                    }}
                  >
                    <Typography
                      variant='subtitle2'
                      sx={{
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      {userInfo?.name || "User"}
                    </Typography>

                    <Typography
                      variant='caption'
                      sx={{
                        color: "#6B7280",
                        display: "block",
                      }}
                    >
                      {userInfo?.email || "user@example.com"}
                    </Typography>

                    <Box
                      sx={{
                        pt: 1.5,
                      }}
                    >
                      {creditLimit > 0 && (
                        <>
                          <LinearProgress
                            variant='determinate'
                            value={creditPercentage}
                            aria-label='Credits remaining'
                            sx={{
                              height: 7,
                              borderRadius: 4,

                              backgroundColor: "#E5E7EB",

                              "& .MuiLinearProgress-bar": {
                                borderRadius: 4,

                                backgroundColor: isCreditsExhausted
                                  ? "#DC2626"
                                  : isCriticalCredits
                                    ? "#EF4444"
                                    : isLowCredits
                                      ? "#F59E0B"
                                      : "#2563EB",
                              },
                            }}
                          />

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              mt: 0.75,
                            }}
                          >
                            <Typography
                              variant='caption'
                              sx={{
                                color: "#6B7280",
                                fontSize: "10px",
                              }}
                            >
                              {creditPercentage}% remaining
                            </Typography>

                            <Typography
                              variant='caption'
                              sx={{
                                color: "#6B7280",
                                fontSize: "10px",
                              }}
                            >
                              {creditsUsed} used
                            </Typography>
                          </Box>
                        </>
                      )}

                      {isCreditsExhausted && (
                        <Box
                          sx={{
                            mt: 1,

                            p: 1.25,

                            borderRadius: 1,

                            backgroundColor: "#FEF2F2",

                            border: "1px solid #FCA5A5",
                          }}
                        >
                          <Typography
                            variant='caption'
                            sx={{
                              color: "#991B1B",

                              fontWeight: 600,

                              display: "block",
                            }}
                          >
                            ⚠ Credits exhausted
                          </Typography>

                          <Typography
                            variant='caption'
                            sx={{
                              color: "#B91C1C",

                              display: "block",

                              mt: 0.25,

                              lineHeight: 1.4,
                            }}
                          >
                            Your subscription is still active, but credit-based
                            features are unavailable.
                          </Typography>
                        </Box>
                      )}

                      {isLowCredits && !isCreditsExhausted && (
                        <Typography
                          variant='caption'
                          sx={{
                            display: "block",

                            mt: 0.75,

                            color: "#B45309",

                            fontWeight: 500,
                          }}
                        >
                          Your credits are running low.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,

                      borderBottom: "1px solid #F3F4F6",
                    }}
                  >
                    {isSubscribed ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",

                          gap: 1,

                          backgroundColor: isCreditsExhausted
                            ? "#FFFBEB"
                            : "#ECFDF5",

                          border: isCreditsExhausted
                            ? "1px solid #FCD34D"
                            : "1px solid #A7F3D0",

                          borderRadius: 1.5,

                          px: 1.5,
                          py: 1,

                          mb: 1.5,
                        }}
                      >
                        {isCreditsExhausted ? (
                          <AlertTriangle
                            size={16}
                            style={{
                              color: "#D97706",

                              flexShrink: 0,
                            }}
                          />
                        ) : (
                          <CheckCircle
                            size={16}
                            style={{
                              color: "#059669",

                              flexShrink: 0,
                            }}
                          />
                        )}

                        <Box>
                          <Typography
                            variant='caption'
                            sx={{
                              fontWeight: 600,

                              color: isCreditsExhausted ? "#92400E" : "#065F46",

                              display: "block",
                            }}
                          >
                            {isCreditsExhausted
                              ? "Subscription Active"
                              : "Active Subscription"}
                          </Typography>

                          <Typography
                            variant='caption'
                            sx={{
                              fontSize: "11px",

                              color: isCreditsExhausted ? "#B45309" : "#047857",
                            }}
                          >
                            Till: {expiryDateObj?.toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    ) : isExpired ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",

                          gap: 1,

                          backgroundColor: "#FEF2F2",

                          border: "1px solid #FCA5A5",

                          borderRadius: 1.5,

                          px: 1.5,
                          py: 1,

                          mb: 1.5,
                        }}
                      >
                        <AlertTriangle
                          size={16}
                          style={{
                            color: "#DC2626",

                            flexShrink: 0,
                          }}
                        />

                        <Box>
                          <Typography
                            variant='caption'
                            sx={{
                              fontWeight: 600,

                              color: "#991B1B",

                              display: "block",
                            }}
                          >
                            Subscription Expired
                          </Typography>

                          <Typography
                            variant='caption'
                            sx={{
                              fontSize: "11px",

                              color: "#B91C1C",
                            }}
                          >
                            Expired: {expiryDateObj?.toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography
                        variant='caption'
                        sx={{
                          color: "#6B7280",
                          fontWeight: 500,

                          display: "block",

                          mb: 1,

                          textAlign: "center",
                        }}
                      >
                        You are not subscribed yet.
                      </Typography>
                    )}

                    <Button
                      fullWidth
                      variant='contained'
                      size='small'
                      onClick={() => {
                        setShowUserMenu(false);

                        navigate("/app/subscriptions");
                      }}
                      startIcon={
                        isCreditsExhausted ? (
                          <Zap size={16} />
                        ) : (
                          <CreditCard size={16} />
                        )
                      }
                      sx={{
                        backgroundColor: isCreditsExhausted
                          ? "#D97706"
                          : isExpired
                            ? "#DC2626"
                            : "#0073A6",

                        textTransform: "none",

                        fontWeight: 600,

                        fontSize: "0.8125rem",

                        borderRadius: 1.5,

                        py: 0.8,

                        "&:hover": {
                          backgroundColor: isCreditsExhausted
                            ? "#B45309"
                            : isExpired
                              ? "#B91C1C"
                              : "#005880",
                        },
                      }}
                    >
                      {isCreditsExhausted
                        ? "Get More Credits"
                        : isExpired
                          ? "Renew Subscription"
                          : isSubscribed
                            ? "Manage Subscription"
                            : "Subscribe Plan"}
                    </Button>
                  </Box>

                  <MenuItem
                    onClick={handleProfileClick}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#374151",

                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <User
                      size={18}
                      style={{
                        marginRight: 8,
                        color: "#6B7280",
                      }}
                    />

                    <Typography variant='body2'>Profile</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={handleTermsClick}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#374151",

                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <FileText
                      size={18}
                      style={{
                        marginRight: 8,
                        color: "#6B7280",
                      }}
                    />

                    <Typography variant='body2'>Terms of Service</Typography>
                  </MenuItem>

                  <MenuItem
                    onClick={handlePrivacyPolicyClick}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#374151",

                      "&:hover": {
                        backgroundColor: "#F9FAFB",
                      },
                    }}
                  >
                    <Shield
                      size={18}
                      style={{
                        marginRight: 8,
                        color: "#6B7280",
                      }}
                    />

                    <Typography variant='body2'>Privacy Policy</Typography>
                  </MenuItem>

                  <Divider sx={{ my: 1 }} />

                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      px: 3,
                      py: 1.5,
                      color: "#DC2626",

                      "&:hover": {
                        backgroundColor: "#FEF2F2",
                      },
                    }}
                  >
                    <LogOut
                      size={18}
                      style={{
                        marginRight: 8,
                        color: "#DC2626",
                      }}
                    />

                    <Typography variant='body2'>Logout</Typography>
                  </MenuItem>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={creditSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCreditSnackbarClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={handleCreditSnackbarClose}
          severity={creditSnackbar.severity}
          variant='filled'
          sx={{
            width: "100%",

            minWidth: {
              xs: "280px",
              sm: "360px",
            },
          }}
          action={
            !isExpired &&
            (isCreditsExhausted || isCriticalCredits || isLowCredits) ? (
              <Button
                size='small'
                color='inherit'
                onClick={() => {
                  setCreditSnackbar((prev) => ({
                    ...prev,
                    open: false,
                  }));

                  navigate("/app/subscriptions");
                }}
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  whiteSpace: "nowrap",
                }}
              >
                Get More Credits
              </Button>
            ) : null
          }
        >
          <Typography
            variant='body2'
            sx={{
              fontWeight: 700,
            }}
          >
            {creditSnackbar.title}
          </Typography>

          <Typography
            variant='caption'
            sx={{
              display: "block",
              mt: 0.25,
            }}
          >
            {creditSnackbar.message}
          </Typography>
        </Alert>
      </Snackbar>

      <CreditsInfoDialog open={isModalOpen} onClose={handleModalClose} />

      <CongratulationDialog
        open={congratsModalOpen}
        onClose={handleCongratsModalClose}
        credits={100}
      />

      <FeedbackDialog
        open={openFeedbackDialog}
        onClose={() => setOpenFeedbackDialog(false)}
      />

      {profileOpen && (
        <ProfileModal
          open={profileOpen}
          onClose={() => setProfileOpen(false)}
        />
      )}

      <TermsDialog
        open={openTermsDialog}
        onClose={() => setOpenTermsDialog(false)}
      />

      <PrivacyPolicyDialog
        open={openPrivacyPolicyDialog}
        onClose={() => setOpenPrivacyPolicyDialog(false)}
      />

      <ToastContainer />
    </>
  );
};

export default Header;
